import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import BPService from "../../../../services/resource/bpService";
import { formatarMoeda } from "../../../../utils/solicitacoes/formatters";
import { classeSaldo, mesAtual } from "../../../../utils/solicitacoes/orcamento";
import Input from "../../../../componentes/input/input";
import InputMoney from "../../../../componentes/input/inputMoney";

const bpService = new BPService();
const MES_ATUAL = mesAtual();
const DEBOUNCE_MS = 200;

/** O /fluig pode retornar mais de uma linha para a mesma conta+centro custo (ex: responsáveis diferentes); mantém só uma. */
function deduplicarPorContaCentroCusto(lista) {
    const vistos = new Map();
    for (const conta of lista) {
        const chave = `${conta.contaReduzido}-${conta.centroCustoCodigo}`;
        if (!vistos.has(chave)) vistos.set(chave, conta);
    }
    return Array.from(vistos.values());
}

function useDebounced(valor, delay) {
    const [debounced, setDebounced] = useState(valor);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(valor), delay);
        return () => clearTimeout(t);
    }, [valor, delay]);
    return debounced;
}

export default function SelecionarContaBpModal({ isOpen, onClose, filiais, onSelecionar, idsJaAdicionados = [], comValor = false }) {
    const [filialId, setFilialId] = useState(null);
    const [contas, setContas] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [filtroConta, setFiltroConta] = useState("");
    const [filtroResponsavel, setFiltroResponsavel] = useState("");
    const [filtroCentroCusto, setFiltroCentroCusto] = useState("");
    const [selecionadaId, setSelecionadaId] = useState(null);
    const [somenteComOrcamento, setSomenteComOrcamento] = useState(false);

    useEffect(() => {
        if (isOpen) {
            selecionarFilial(Number(localStorage.getItem("filialId")));
        }
    }, [isOpen]);
    // Número da nota fiscal persiste entre várias adições na mesma sessão do modal — uma nota
    // costuma ser dividida entre várias contas. O valor é específico de cada linha, por isso limpa
    // a cada adição.
    const [numeroNotaFiscal, setNumeroNotaFiscal] = useState("");
    const [valorRateio, setValorRateio] = useState("");

    const filtroContaDebounced = useDebounced(filtroConta, DEBOUNCE_MS);
    const filtroCentroCustoDebounced = useDebounced(filtroCentroCusto, DEBOUNCE_MS);
    const filtroResponsavelDebounced = useDebounced(filtroResponsavel, DEBOUNCE_MS);

    function selecionarFilial(id) {
        setFilialId(id);
        setContas([]);
        setSelecionadaId(null);
        const filial = filiais.find(f => f.id === id);
        if (!filial) return;

        setCarregando(true);
        bpService.listarParaFluig(filial.codigo)
            .then(res => setContas(deduplicarPorContaCentroCusto(res.data)))
            .catch(() => {
                toast.error("Erro ao buscar contas BP da filial.");
                setContas([]);
            })
            .finally(() => setCarregando(false));
    }

    const contasFiltradas = useMemo(() => {
        const termoConta = filtroContaDebounced.trim().toLowerCase();
        const termoResponsavel = filtroResponsavelDebounced.trim().toLowerCase();
        const termoCentroCusto = filtroCentroCustoDebounced.trim().toLowerCase();
        return contas.filter(conta => {
            const matchConta = !termoConta
                || String(conta.contaReduzido ?? "").toLowerCase().includes(termoConta)
                || (conta.contaDescricao ?? "").toLowerCase().includes(termoConta);
            const matchResponsavel = !termoResponsavel
                || String(conta.responsavelNome ?? "").toLowerCase().includes(termoResponsavel)
                || (conta.responsavelNomeUsuario ?? "").toLowerCase().includes(termoResponsavel);
            const matchCentroCusto = !termoCentroCusto
                || String(conta.centroCustoCodigo ?? "").toLowerCase().includes(termoCentroCusto)
                || (conta.centroCustoDescricao ?? "").toLowerCase().includes(termoCentroCusto);
            const matchOrcamento = !somenteComOrcamento || (conta.valoresMensais?.[MES_ATUAL] ?? 0) > 0;
            return matchConta && matchCentroCusto && matchResponsavel && matchOrcamento;
        });
    }, [contas, filtroContaDebounced, filtroCentroCustoDebounced, filtroResponsavelDebounced, somenteComOrcamento]);

    const filtrosAtivos = filtroConta.trim() !== "" || filtroCentroCusto.trim() !== "" || filtroResponsavel.trim() !== "";

    function limparFiltros() {
        setFiltroConta("");
        setFiltroCentroCusto("");
        setFiltroResponsavel("");
    }

    // Quando comValor, a mesma conta pode se repetir em notas fiscais diferentes — a duplicidade
    // real é a combinação Conta BP + Nota Fiscal, não a conta isolada.
    function chaveConta(conta) {
        return comValor ? `${conta.id}|${numeroNotaFiscal.trim()}` : conta.id;
    }

    function selecionarLinha(conta) {
        if (idsJaAdicionados.includes(chaveConta(conta))) {
            if (comValor) toast.error("Esta conta BP já foi adicionada para esta nota fiscal.");
            return;
        }
        setSelecionadaId(prev => prev === conta.id ? null : conta.id);
    }

    function adicionar(conta) {
        const orcamentoMes = conta.valoresMensais?.[MES_ATUAL] ?? 0;
        const realizadoMes = conta.despesasMensaisAtual?.[MES_ATUAL] ?? 0;
        onSelecionar({
            baseBpId: conta.id,
            filialNome: conta.filialNome,
            centroCustoCodigo: conta.centroCustoCodigo,
            centroCustoDescricao: conta.centroCustoDescricao,
            contaReduzido: conta.contaReduzido,
            contaContabilDescricao: conta.contaDescricao,
            responsavelNome: conta.responsavelNome,
            orcamentoMes,
            valorRealizado: realizadoMes,
            saldoAtual: orcamentoMes - realizadoMes,
            ...(comValor ? { numeroNotaFiscal: numeroNotaFiscal.trim(), valor: Number(valorRateio) } : {}),
        });
        setSelecionadaId(null);
        setValorRateio("");
    }

    function adicionarSelecionada() {
        const conta = contas.find(c => c.id === selecionadaId);
        if (!conta || idsJaAdicionados.includes(chaveConta(conta))) return;
        adicionar(conta);
    }

    function escolherDuploClique(conta) {
        // Em modo comValor, a inclusão exige nota fiscal + valor preenchidos — não dá pra concluir
        // com um único duplo-clique, então esse atalho fica restrito ao modo simples (Etapa 1 de Compras).
        if (comValor) return;
        if (idsJaAdicionados.includes(chaveConta(conta))) return;
        adicionar(conta);
    }

    const podeAdicionar = !!selecionadaId
        && (!comValor || (numeroNotaFiscal.trim() !== ""));

    if (!isOpen) return null;

    return (
        <div className="rounded-lg bg-white border border-border flex flex-col  pb-3 mb-3">
            <div className="bg-muted rounded-t-lg border-b border-border  px-3 pt-2  text-sm text-muted-foreground ">
                <h1>Adicionar Conta BP</h1>
                <label className="text-xs">Selecione a filial, filtre e escolha as contas que farão parte do rateio</label>
            </div>
            <div className="px-3 py-4 flex flex-col gap-4">

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">

                    <CustomDropdown label="Filial" value={filialId} onChange={e => selecionarFilial(e.value)} options={filiais} optionValue="id" optionLabel="nome" placeholder="Selecione a filial" filter />

                    <Input
                        type="text"
                        label="Centro Custo"
                        value={filtroCentroCusto}
                        onChange={e => setFiltroCentroCusto(e.target.value)}
                        placeholder="Ex: 280 ou Distribuição"
                        disabled={filialId && !carregando && contas.length > 0 ? false : true}
                    />

                    <Input
                        type="text"
                        label="Conta"
                        value={filtroConta}
                        onChange={e => setFiltroConta(e.target.value)}
                        placeholder="Ex: 4246 ou Manutenção"
                        disabled={filialId && !carregando && contas.length > 0 ? false : true}
                    />

                    <Input
                        type="text"
                        label="Responsável"
                        value={filtroResponsavel}
                        onChange={e => setFiltroResponsavel(e.target.value)}
                        placeholder="Nome do Responsável da Conta"
                        disabled={filialId && !carregando && contas.length > 0 ? false : true}
                    />

                    <label className="flex items-end gap-1 cursor-pointer select-none text-xs text-muted-foreground">
                        <input
                            type="checkbox"
                            checked={somenteComOrcamento}
                            onChange={e => setSomenteComOrcamento(e.target.checked)}
                            className="h-3 w-3 mb-0.5"
                        />
                        Somente com orçamento
                    </label>

                </div>

                {filialId && !carregando && contas.length > 0 && (
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground flex items-center gap-2">
                            {contasFiltradas.length} conta{contasFiltradas.length === 1 ? "" : "s"} encontrada{contasFiltradas.length === 1 ? "" : "s"}

                        </span>
                        <div>
                            {filtrosAtivos && (
                                <button type="button" onClick={limparFiltros} className="text-xs text-primary hover:underline">
                                    Limpar filtros
                                </button>
                            )}

                        </div>
                    </div>
                )}

                {carregando && (
                    <div className="text-sm text-muted-foreground text-center py-6">Carregando contas...</div>
                )}

                {!carregando && filialId && contas.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">Nenhuma conta BP encontrada para esta filial.</div>
                )}

                {!carregando && contas.length > 0 && (
                    <div className="overflow-y-auto border border-border rounded-lg max-h-[170px]">
                        <table className="w-full text-xs">
                            <thead className="bg-muted sticky top-0">
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 font-semibold">Filial</th>
                                    <th className="px-3 py-2 font-semibold">Responsável</th>
                                    <th className="px-3 py-2 font-semibold">Centro Custo</th>
                                    <th className="px-3 py-2 font-semibold">Conta</th>
                                    <th className="px-3 py-2 font-semibold text-right">Orçamento</th>
                                    <th className="px-3 py-2 font-semibold text-right">Realizado</th>
                                    <th className="px-3 py-2 font-semibold text-right">Saldo</th>
                                    <th className="px-3 py-2 w-9" />
                                </tr>
                            </thead>
                            <tbody>
                                {contasFiltradas.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-6 text-center text-sm text-muted-foreground">
                                            Nenhuma conta encontrada para os filtros informados.
                                        </td>
                                    </tr>
                                )}
                                {contasFiltradas.map(conta => {
                                    const orcamentoMes = conta.valoresMensais?.[MES_ATUAL] ?? 0;
                                    const realizadoMes = conta.despesasMensaisAtual?.[MES_ATUAL] ?? 0;
                                    const saldo = orcamentoMes - realizadoMes;
                                    const jaAdicionada = idsJaAdicionados.includes(chaveConta(conta));
                                    const selecionada = selecionadaId === conta.id;
                                    return (
                                        <tr
                                            key={conta.id}
                                            onClick={() => selecionarLinha(conta)}
                                            onDoubleClick={() => escolherDuploClique(conta)}
                                            className={`border-t border-border ${jaAdicionada
                                                ? "bg-success/5 cursor-default"
                                                : selecionada
                                                    ? "bg-primary/10 ring-1 ring-inset ring-primary/40 cursor-pointer"
                                                    : "hover:bg-primary/5 cursor-pointer"
                                                }`}
                                        >
                                            <td className="px-3 py-2 text-foreground text-nowrap">{conta.filialNome}</td>
                                            <td className="px-3 py-2 text-foreground">{conta.responsavelNomeUsuario}</td>
                                            <td className="px-3 py-2 text-foreground">{conta.centroCustoCodigo} · {conta.centroCustoDescricao}</td>
                                            <td className="px-3 py-2 text-foreground">{conta.contaReduzido} · {conta.contaDescricao}</td>
                                            <td className="px-3 py-2 text-right text-foreground">{formatarMoeda(orcamentoMes)}</td>
                                            <td className="px-3 py-2 text-right text-foreground">{formatarMoeda(realizadoMes)}</td>
                                            <td className={`px-3 py-2 text-right font-semibold ${classeSaldo(saldo, orcamentoMes)}`}>{formatarMoeda(saldo)}</td>
                                            <td className="px-3 py-2 text-center">
                                                {jaAdicionada && <i className="fa fa-check text-success" title="Já adicionada" />}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="flex items-end justify-end gap-2 px-3">
                {numeroNotaFiscal && <span className="pb-2 text-xs text-gray-500">Número da Nota Fiscal: </span>}
                {comValor && (
                    <Input
                        type="text"
                        value={numeroNotaFiscal}
                        onChange={e => setNumeroNotaFiscal(e.target.value)}
                        placeholder="Número da Nota Fiscal"
                    />
                )}

                {comValor && (
                    <InputMoney
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        mode="decimal"
                        onFocus={(e) => e.target.select()}
                        min="0"
                        placeholder="(R$) Valor do Rateio"
                        value={valorRateio}
                        onChange={e => setValorRateio(e.target.value)}
                        disabled={!selecionadaId}
                    />
                )}
                <Button variant="outline" label="Fechar" onClick={onClose} />
                <Button variant="blue" label="Adicionar" iconLeft="fa fa-plus" disabled={!podeAdicionar} onClick={adicionarSelecionada} />
            </div>
        </div>
    );
}
